import React, {useState} from 'react';
import {Col, Form} from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import {MultiValue} from 'react-select/dist/declarations/src/types';
import Button from '@/components/ui/Button';
import {BaseSettingsForm} from '@/components/forms';
import {useStoreActions, useStoreState} from 'store';
import {useAlert} from '@/components/alerts';
import axios, {AxiosResponse} from 'axios';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import memoize from 'memoize-one';
import * as yup from 'yup';
import {UserSearchResult} from 'entities/user';
import {StoryboardAccessType} from 'entities/storyboard';
import {Story} from 'entities/story';
import {shareStory, StoryUserAccessPostData, UserSelectOption} from '@/views/stories/api/stories';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import i18n from 'i18n';

interface Props {
    storyId: number | null;
    onFinish: () => void;
}

interface UserSearchParams {
    query: string;
}

type OptionType = {
    label: string;
    value: string;
    isDisabled?: boolean;
}

type userSearchRequest = (inputValue: string) => Promise<AxiosResponse<UserSearchResult[], UserSearchParams>>;
const loadStoryUsersRequest: userSearchRequest = (inputValue) =>
    axios.get<UserSearchResult[], AxiosResponse<UserSearchResult[], UserSearchParams>, UserSearchParams>(
        window.URLS.userSearch, {
        params: {
            query: inputValue,
        },
    })
        .catch((error) => {
            if (error.response.status === 400) {
                return error.response;
            }

            throw error;
        });
const loadStoryUsersRequestDebounced = AwesomeDebouncePromise<userSearchRequest>(loadStoryUsersRequest, 280);

const StorySharingFormSchema: yup.Schema<StoryUserAccessPostData> = yup.object({
    users: yup.array().of(
        yup.object({
            value: yup.number().required(),
            label: yup.string().required(),
        }).required(),
    ).required(i18n.t('user_selection_required.text')),
    accessType: yup.object({
        value: yup
            .mixed<StoryboardAccessType>()
            .oneOf<StoryboardAccessType>(Object.values(StoryboardAccessType) as Array<StoryboardAccessType>)
            .required(),
        label: yup.string().required(),
    }).required(),
});


const StorySharingForm = React.memo<Props>(({
    storyId,
    onFinish,
}) => {
    const {showError} = useAlert();
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const currentUserId = userData?.id;

    const stories = useStoreState((state) => state.stories.items);
    const editStoryAccess = useStoreActions((actions) => actions.stories.editStoryAccess);

    const [userOptions, setUserOptions] = useState<UserSelectOption[]>([]);

    const activeStory = memoize<(id: number | null) => Story | null>(
        (id) => stories.find((s) => s.id === id) ?? null,
    );

    const initialValues: StoryUserAccessPostData = {
        users: [],
        accessType: {
            label: i18n.t('jt_access_view.text'),
            value: StoryboardAccessType.Read,
        },
    };

    // Custom error handling for this specific form
    const customErrorHandling = (error: any, formik: any) => {
        // Handle any specific error cases for story sharing
        if (error.response?.status === 400) {
            const errorData = error.response?.data;
            if (errorData?.message) {
                formik.setFieldError('users', errorData.message);
            }
            return true;
        }
        return false;
    };

    // Custom success handling
    const customSuccessHandling = (response: any, actionName: string) => {
        if (storyId) {
            editStoryAccess([storyId, response.data.visibleTo]);
        }
        onFinish();
    };

    const getUserOptions = async (inputValue: string): Promise<UserSelectOption[]> => {
        const results = await loadStoryUsersRequestDebounced(inputValue);

        const selectAllOption = {
            label: i18n.t('select_all_label.text'),
            value: '*',
        };

        const userChoices = results.data.filter(user => user.id !== currentUserId).map((user) => ({
            label: `${user.identifier} (${user.email})`,
            value: user.id,
        }));
        setUserOptions(userChoices);

        return [
            ...(inputValue || !userChoices.length ? [] : [selectAllOption]),
            ...userChoices,
        ];
    };

    const onUserSelection = (formik: any) => (value: MultiValue<UserSelectOption>): void => {
        if (value && value.length && value[value.length - 1].value === '*') {
            formik.setFieldValue('users', userOptions);
        } else {
            formik.setFieldValue('users', value || []);
        }
    };

    // Custom submission handler for non-CRUD API
    const customSubmissionHandler = async (values: StoryUserAccessPostData, formik: any) => {
        if (!storyId) {
            showError(i18n.t('story_sharing_something_went.text'));
            throw new Error(i18n.t('story_sharing_cannot_give_access.text'));
        }

        const response = await shareStory(storyId as number, values);
        return response;
    };

    return (
        <BaseSettingsForm
            resourceUrl="" // Not used with customSubmissionHandler
            storeActionPath="stories.editStoryAccess"
            validationSchema={StorySharingFormSchema as any}
            initialValues={initialValues}
            successMessageKey="story_shared_successfully.text"
            onSave={onFinish}
            customErrorHandling={customErrorHandling}
            customSuccessHandling={customSuccessHandling}
            customSubmissionHandler={customSubmissionHandler}
        >
            {(formik, loading) => (
                <div className='form-row'>
                    <Form.Group className="mb-3" controlId="users" as={Col} xs="6" >
                        <AsyncSelect<UserSelectOption, true>
                            id="story-sharing-form-user-select"
                            isMulti
                            cacheOptions
                            defaultOptions
                            loadOptions={getUserOptions}
                            hideSelectedOptions={false}
                            onChange={onUserSelection(formik)}
                            value={formik.values.users}
                            styles={{
                                valueContainer: (provided) => ({
                                    ...provided,
                                    maxHeight: '100px',
                                    overflowY: 'scroll',
                                    scrollbarWidth: 'none',
                                    overflowStyle: 'none',
                                    '&::-webkit-scrollbar': {
                                        width: 0,
                                        height: 0,
                                    },
                                }),
                            }}
                        />
                        <Form.Control.Feedback
                            type="invalid"
                            style={{display: formik.errors.users ? 'block' : 'none'}}
                        >
                            {typeof formik.errors.users === 'string' ? formik.errors.users : ''}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="userAccessType" as={Col} xs="4" >
                        <Select<OptionType>
                            id="story-sharing-form-access-type-select"
                            value={formik.values.accessType}
                            options={[
                                {
                                    label: i18n.t('jt_access_view.text'),
                                    value: 'read',
                                },
                                {
                                    label: i18n.t('jt_access_edit_and_delete.text'),
                                    value: 'write',
                                    isDisabled: activeStory(storyId) ? !activeStory(storyId)?.userIsAdmin : true,
                                },
                            ]}
                            onChange={(value) => formik.setFieldValue('accessType', value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="storyTitle-save" as={Col} xs="2" >
                        <Button
                            variant="info"
                            size="sm"
                            fullWidth
                            type="submit"
                            disabled={formik.isSubmitting}
                            loading={formik.isSubmitting}
                            data-cy="story-sharing-submit-btn"
                        >
                            {i18n.t('story_share_js.text')}
                        </Button>
                    </Form.Group >
                </div >
            )}
        </BaseSettingsForm >
    );
});


export default StorySharingForm;
